import { createFileRoute, useNavigate, redirect } from "@tanstack/react-router";
import { useState, useMemo, useEffect } from "react";
import { PageHeader, fmt } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ReceiptText,
  Search,
  Banknote,
  Smartphone,
  CreditCard,
  CheckCircle2,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  PackageCheck,
  Plus,
  Minus,
  Trash2,
  ShoppingCart,
  Printer,
  FileText,
  History,
  UserPlus,
} from "lucide-react";
import { toast } from "sonner";
import { hasRole, useAuth } from "@/lib/auth-store";
import {
  tabStore,
  useTabStore,
  type Tab,
  type TabPayment,
} from "@/lib/tab-store";
import { useStore } from "@/lib/pos-store";
import { useRealtimeTabs, markLocalAction } from "@/hooks/useRealtimeTabs";

export const Route = createFileRoute("/tabs-admin")({
  beforeLoad: ({ context }) => {
    if (!hasRole(context.user, "cashier")) {
      throw redirect({ to: "/" });
    }
  },
  head: () => ({
    meta: [
      { title: "Orders — Staff" },
      {
        name: "description",
        content: "View orders and issue items to customers.",
      },
    ],
  }),
  component: TabsAdminPage,
});

const paymentMethods: {
  value: TabPayment["method"];
  label: string;
  icon: typeof Banknote;
}[] = [
  { value: "cash", label: "Cash", icon: Banknote },
  { value: "mpesa", label: "M-Pesa", icon: Smartphone },
  { value: "bank", label: "Bank", icon: CreditCard },
];

function TabsAdminPage() {
  const { user } = useAuth();
  const allTabs = useTabStore((s) => s.tabs);

  // Listen for real-time updates from other clients
  useRealtimeTabs();

  // Load all tabs for staff view when component mounts
  useEffect(() => {
    tabStore.initStaff();
  }, []);
  const openTabs = allTabs.filter((t) => t.status === "open");
  const closedTabs = allTabs
    .filter((t) => t.status === "closed")
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(0, 20);
  const items = useStore((s) => s.items);
  const navigate = useNavigate();
  const [searchQ, setSearchQ] = useState("");
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState<TabPayment["method"]>("cash");
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [payTabSnapshot, setPayTabSnapshot] = useState<Tab | null>(null);
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);
  const [tabDetailOpen, setTabDetailOpen] = useState<Tab | null>(null);
  const [closedTabOpen, setClosedTabOpen] = useState<string | null>(null);
  const [newCustomerName, setNewCustomerName] = useState("");
  const [newCustomerDialog, setNewCustomerDialog] = useState(false);

  // Add-item menu cart
  const [addItemSearch, setAddItemSearch] = useState("");
  const [addItemCart, setAddItemCart] = useState<
    { itemId: string; name: string; qty: number; unitPrice: number }[]
  >([]);

  const filtered = openTabs.filter(
    (t) =>
      t.customerName.toLowerCase().includes(searchQ.toLowerCase()) ||
      t.id.toLowerCase().includes(searchQ.toLowerCase()),
  );

  const filteredAddItems = useMemo(
    () =>
      items.filter(
        (i) =>
          i.stock > 0 &&
          (i.name.toLowerCase().includes(addItemSearch.toLowerCase()) ||
            i.sku.toLowerCase().includes(addItemSearch.toLowerCase())),
      ),
    [items, addItemSearch],
  );

  const addItemCartTotal = addItemCart.reduce(
    (a, l) => a + l.qty * l.unitPrice,
    0,
  );

  const handleAddToCart = (itemId: string) => {
    setAddItemCart((c) => {
      const ex = c.find((l) => l.itemId === itemId);
      if (ex)
        return c.map((l) =>
          l.itemId === itemId ? { ...l, qty: l.qty + 1 } : l,
        );
      const it = items.find((i) => i.id === itemId)!;
      return [
        ...c,
        { itemId, name: it.name, qty: 1, unitPrice: it.sellingPrice },
      ];
    });
  };

  const handleSubmitAddItems = async () => {
    if (!tabDetailOpen || addItemCart.length === 0) return;
    markLocalAction();
    for (const l of addItemCart) {
      await tabStore.staffAddToTab(
        tabDetailOpen.id,
        l.itemId,
        l.name,
        l.qty,
        l.unitPrice,
      );
    }
    toast.success(
      `${addItemCart.reduce((a, l) => a + l.qty, 0)} item(s) added to bill`,
    );
    setAddItemCart([]);
    setAddItemSearch("");
    setAddItemDialogOpen(false);
    setTabDetailOpen(
      tabStore.getState().tabs.find((t) => t.id === tabDetailOpen.id) ?? null,
    );
  };

  const handlePay = async () => {
    const activeTab = payTabSnapshot || tabDetailOpen;
    if (!activeTab || !payAmount || +payAmount <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    markLocalAction();
    const updated = await tabStore.staffPayTab(
      activeTab.id,
      +payAmount,
      payMethod,
    );
    if (updated) {
      toast.success(`Payment of ${fmt(+payAmount)} recorded`);
      setPayAmount("");
      setPayDialogOpen(false);
      setTabDetailOpen(
        tabStore.getState().tabs.find((t) => t.id === tabDetailOpen.id) ?? null,
      );
    }
  };

  const handleIssueAll = async () => {
    if (!tabDetailOpen) return;
    markLocalAction();
    const success = await tabStore.issueItems(tabDetailOpen.id);
    if (success) {
      toast.success(`Items issued — stock reduced`);
      setTabDetailOpen(
        tabStore.getState().tabs.find((t) => t.id === tabDetailOpen.id) ?? null,
      );
    } else {
      const unissued = tabDetailOpen.items.filter((i) => !i.issued);
      if (unissued.length === 0) {
        toast.info("All items already issued");
      } else {
        toast.error("Insufficient stock to issue some items");
      }
    }
  };

  const handleIssueSingle = async (itemId: string) => {
    if (!tabDetailOpen) return;
    markLocalAction();
    const success = await tabStore.issueSingleItem(tabDetailOpen.id, itemId);
    if (success) {
      toast.success(`Item issued`);
      setTabDetailOpen(
        tabStore.getState().tabs.find((t) => t.id === tabDetailOpen.id) ?? null,
      );
    } else {
      toast.error(
        "Cannot issue this item — insufficient stock or already issued",
      );
    }
  };

  const handleCloseTab = async (tabId: string) => {
    const tab = openTabs.find((t) => t.id === tabId);
    if (!tab) return;
    const unissued = tab.items.filter((i) => !i.issued);
    if (unissued.length > 0) {
      toast.error(`${unissued.length} item(s) not yet issued!`);
      return;
    }
    const balance =
      Number(tab.items.reduce((a, i) => a + Number(i.total), 0)) -
      Number(tab.payments.reduce((a, p) => a + Number(p.amount), 0));
    if (balance > 0) {
      toast.error(`${tab.customerName} still owes ${fmt(balance)}`);
      return;
    }
    markLocalAction();
    await tabStore.staffCloseTab(tabId);
    toast.success(`${tab.customerName}'s bill closed`);
    setTabDetailOpen(null);
  };

  const handleCreateBill = async () => {
    const name = newCustomerName.trim();
    if (!name) {
      toast.error("Enter customer name");
      return;
    }
    markLocalAction();
    const tab = await tabStore.staffCreateTab(name);
    if (tab) {
      toast.success(`Bill opened for ${name}`);
      setNewCustomerName("");
      setNewCustomerDialog(false);
      setTabDetailOpen(tab);
    }
  };

  /** Print an invoice for a tab (works for open or closed tabs) */
  const handlePrintInvoice = (tab: Tab) => {
    const printWin = window.open("", "_blank", "width=400,height=600");
    if (!printWin) return;

    const total = Number(tab.items.reduce((a, i) => a + Number(i.total), 0));
    const paid = Number(tab.payments.reduce((a, p) => a + Number(p.amount), 0));
    const balance = total - paid;

    const fmtHtml = (n: number) =>
      new Intl.NumberFormat("en-KE", {
        style: "currency",
        currency: "KES",
        maximumFractionDigits: 0,
      }).format(n);

    const itemsHtml = tab.items
      .map(
        (item) => `
        <tr>
          <td style="padding:3px 0;">
            ${item.name}
            <br><span style="color:#888;font-size:10px;">${Number(item.qty)} × ${fmtHtml(Number(item.unitPrice))}</span>
          </td>
          <td style="text-align:right;padding:3px 0;white-space:nowrap;">${fmtHtml(Number(item.total))}</td>
        </tr>`,
      )
      .join("");

    const paymentsHtml = tab.payments
      .map(
        (p) => `
        <tr>
          <td style="padding:3px 0;text-transform:capitalize">${p.method}</td>
          <td style="text-align:right;padding:3px 0;white-space:nowrap;">${fmtHtml(Number(p.amount))}</td>
        </tr>`,
      )
      .join("");

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Invoice</title>
  <style>
    @page { margin: 0; }
    body {
      font-family: 'Courier New', Courier, monospace;
      font-size: 12px;
      margin: 0;
      padding: 32px 16px 16px;
      display: flex;
      flex-direction: column;
      align-items: center;
      background: white;
      color: black;
    }
    .invoice-body { width: 80mm; max-width: 100%; }
    .center { text-align: center; }
    .bold { font-weight: bold; }
    .line { border-top: 1px dashed #999; margin: 8px 0; }
    table { width: 100%; border-collapse: collapse; }
    td:last-child { text-align: right; white-space: nowrap; }
    .total td { padding-top: 4px; font-size: 14px; font-weight: bold; }
    .invoice-badge {
      display: inline-block;
      border: 2px solid #333;
      padding: 2px 12px;
      font-size: 14px;
      font-weight: bold;
      letter-spacing: 2px;
      margin-bottom: 4px;
    }
  </style>
</head>
<body>
  <div class="invoice-body">
    <div class="center bold" style="font-size:14px;">White House Lounge</div>
    <div class="center"><span class="invoice-badge">INVOICE</span></div>
    <div class="center" style="color:#888;font-size:10px;">#${tab.id.slice(-6).toUpperCase()}</div>
    <div class="line"></div>
    <div>Bill To: <strong>${tab.customerName}</strong></div>
    <div>Date: ${new Date(Number(tab.createdAt)).toLocaleString()}</div>
    ${tab.waiterId ? `<div>Waiter ID: ${tab.waiterId}</div>` : ""}
    <div class="line"></div>
    <table>
      <tr class="bold"><td>Description</td><td>Amount</td></tr>
      ${itemsHtml}
    </table>
    <div class="line"></div>
    <table>
      <tr><td>Subtotal</td><td>${fmtHtml(total)}</td></tr>
      ${
        tab.payments.length > 0
          ? `
      <tr class="bold"><td>Payments</td><td></td></tr>
      ${paymentsHtml}
      <tr><td>Total Paid</td><td>${fmtHtml(paid)}</td></tr>
      `
          : ""
      }
      <tr class="total"><td>${balance <= 0 ? "SETTLED" : "AMOUNT DUE"}</td><td>${fmtHtml(Math.abs(balance))}</td></tr>
    </table>
    <div class="line"></div>
    <div class="center" style="color:#888;font-size:10px;">Thank you · Karibu tena</div>
    <div class="center" style="color:#888;font-size:9px;margin-top:4px;">This is a computer-generated invoice.</div>
  </div>
</body>
</html>`;

    printWin.document.write(html);
    printWin.document.close();
    printWin.focus();
    printWin.print();
  };

  /** Print a receipt for a closed tab */
  const handlePrintClosedReceipt = (tab: Tab) => {
    const printWin = window.open("", "_blank", "width=400,height=600");
    if (!printWin) return;

    const total = Number(tab.items.reduce((a, i) => a + Number(i.total), 0));
    const paid = Number(tab.payments.reduce((a, p) => a + Number(p.amount), 0));
    const balance = total - paid;

    const fmtHtml = (n: number) =>
      new Intl.NumberFormat("en-KE", {
        style: "currency",
        currency: "KES",
        maximumFractionDigits: 0,
      }).format(n);

    const itemsHtml = tab.items
      .map(
        (item) => `
        <tr>
          <td style="padding:2px 0;">
            ${item.name}
            <br><span style="color:#888;font-size:10px;">${Number(item.qty)} × ${fmtHtml(Number(item.unitPrice))}</span>
          </td>
          <td style="text-align:right;padding:2px 0;white-space:nowrap;">${fmtHtml(Number(item.total))}</td>
        </tr>`,
      )
      .join("");

    const paymentsHtml = tab.payments
      .map(
        (p) => `
        <tr>
          <td style="padding:2px 0;text-transform:capitalize">${p.method}</td>
          <td style="text-align:right;padding:2px 0;white-space:nowrap;">${fmtHtml(Number(p.amount))}</td>
        </tr>`,
      )
      .join("");

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
                      <title>Bill Receipt</title>
  <style>
    @page { margin: 0; }
    body {
      font-family: 'Courier New', Courier, monospace;
      font-size: 12px;
      margin: 0;
      padding: 32px 16px 16px;
      display: flex;
      flex-direction: column;
      align-items: center;
      background: white;
      color: black;
    }
    .receipt-body { width: 80mm; max-width: 100%; }
    .center { text-align: center; }
    .bold { font-weight: bold; }
    .line { border-top: 1px dashed #999; margin: 8px 0; }
    table { width: 100%; border-collapse: collapse; }
    td:last-child { text-align: right; white-space: nowrap; }
    .total td { padding-top: 4px; font-size: 14px; font-weight: bold; }
  </style>
</head>
<body>
  <div class="receipt-body">
    <div class="center bold" style="font-size:14px;">White House Lounge</div>
    <div class="center" style="color:#888;font-size:10px;">BILL CLOSED · #${tab.id.slice(-6).toUpperCase()}</div>
    <div class="line"></div>
    <div>Customer: <strong>${tab.customerName}</strong></div>
    <div>Date: ${new Date(Number(tab.createdAt)).toLocaleString()}</div>
    <div class="line"></div>
    <table>
      <tr class="bold"><td>Item</td><td>Total</td></tr>
      ${itemsHtml}
    </table>
    <div class="line"></div>
    <table>
      <tr class="bold"><td>Payments</td><td></td></tr>
      ${paymentsHtml}
    </table>
    <div class="line"></div>
    <table>
      <tr><td>Total Ordered</td><td>${fmtHtml(total)}</td></tr>
      <tr><td>Total Paid</td><td>${fmtHtml(paid)}</td></tr>
      <tr class="total"><td>${balance <= 0 ? "SETTLED" : "BALANCE"}</td><td>${fmtHtml(Math.abs(balance))}</td></tr>
    </table>
    <div class="center" style="color:#888;font-size:10px;margin-top:16px;">Thank you · Karibu tena</div>
  </div>
</body>
</html>`;

    printWin.document.write(html);
    printWin.document.close();
    printWin.focus();
    printWin.print();
  };

  return (
    <div>
      <PageHeader
        title="Bills"
        subtitle={`${openTabs.length} open bill${openTabs.length !== 1 ? "s" : ""}`}
        right={
          <Dialog open={newCustomerDialog} onOpenChange={setNewCustomerDialog}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-1.5">
                <UserPlus className="h-4 w-4" />
                New Bill
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Open Bill for Customer</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Customer Name</Label>
                  <Input
                    value={newCustomerName}
                    onChange={(e) => setNewCustomerName(e.target.value)}
                    placeholder="e.g. John Doe"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCreateBill();
                    }}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleCreateBill}>Open Bill</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="px-4 pt-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search customer name..."
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
          />
        </div>
      </div>

      {/* Unattended orders alert */}
      {(() => {
        const unattendedCount = openTabs.filter((t) =>
          t.items.some((i) => !i.issued),
        ).length;
        return unattendedCount > 0 ? (
          <div className="mx-4 mt-3 animate-pulse rounded-xl border-2 border-destructive/50 bg-destructive/10 p-3">
            <div className="flex items-center gap-2">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground">
                {unattendedCount}
              </span>
              <p className="text-sm font-semibold text-destructive">
                {unattendedCount} bill{unattendedCount !== 1 ? "s" : ""} with
                unserved items
              </p>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Tap to issue items and register sales
            </p>
          </div>
        ) : null;
      })()}

      <div className="space-y-2 px-4 pt-4">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center pt-16 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-accent">
              <ReceiptText className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="mt-4 font-display text-lg font-semibold">
              No Bills
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {searchQ
                ? "No bills match your search"
                : "Open a new bill for a customer"}
            </p>
          </div>
        ) : (
          filtered.map((tab) => {
            const tabTotal = Number(
              tab.items.reduce((a, i) => a + Number(i.total), 0),
            );
            const tabPaid = Number(
              tab.payments.reduce((a, p) => a + Number(p.amount), 0),
            );
            const balance = tabTotal - tabPaid;
            const unissued = tab.items.filter((i) => !i.issued).length;
            const totalQty = Number(
              tab.items.reduce((a, i) => a + Number(i.qty), 0),
            );
            const hasUnissued = unissued > 0;
            return (
              <button
                key={tab.id}
                onClick={() => setTabDetailOpen(tab)}
                className={`w-full rounded-xl border-2 p-3 text-left active:scale-[0.98] min-h-[56px] ${
                  hasUnissued
                    ? "border-destructive/40 bg-destructive/5 shadow-[0_0_12px_rgba(239,68,68,0.15)]"
                    : "border-border bg-card"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-full font-display text-sm font-bold ${
                      hasUnissued
                        ? "bg-destructive/20 text-destructive animate-pulse"
                        : "bg-accent text-accent-foreground"
                    }`}
                  >
                    {tab.customerName.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">
                      {tab.customerName}
                      {tab.waiterId && (
                        <span className="ml-1.5 text-[10px] text-muted-foreground">
                          (waiter)
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {totalQty} item{totalQty !== 1 ? "s" : ""}
                      {hasUnissued && (
                        <span className="ml-1 font-bold text-destructive">
                          · {unissued} TO SERVE!
                        </span>
                      )}
                      {!hasUnissued && tab.items.length > 0 && (
                        <span className="ml-1 text-primary font-semibold">
                          · All served
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-display text-sm font-semibold">
                      {fmt(balance)}
                    </p>
                    <p
                      className={`text-[10px] ${balance <= 0 ? "text-primary" : "text-muted-foreground"}`}
                    >
                      {balance <= 0 ? "PAID" : "OWES"}
                    </p>
                  </div>
                  {hasUnissued && (
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-destructive text-[11px] font-bold text-destructive-foreground animate-pulse">
                      {unissued}
                    </span>
                  )}
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* Tab Detail Dialog */}
      <Dialog
        open={tabDetailOpen !== null}
        onOpenChange={(v) => {
          if (!v) {
            setTabDetailOpen(null);
            setAddItemCart([]);
            setAddItemSearch("");
          }
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          {tabDetailOpen && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accent text-accent-foreground font-display text-sm font-bold">
                    {tabDetailOpen.customerName.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="truncate">{tabDetailOpen.customerName}</p>
                    {tabDetailOpen.waiterId && (
                      <p className="text-[10px] font-normal text-muted-foreground">
                        Waiter-assisted
                      </p>
                    )}
                  </div>
                </DialogTitle>
              </DialogHeader>

              {(() => {
                const detailTotal = Number(
                  tabDetailOpen.items.reduce((a, i) => a + Number(i.total), 0),
                );
                const detailPaid = Number(
                  tabDetailOpen.payments.reduce(
                    (a, p) => a + Number(p.amount),
                    0,
                  ),
                );
                const detailBalance = detailTotal - detailPaid;
                return (
                  <div className="rounded-xl bg-gradient-to-br from-primary to-primary/70 p-4 text-primary-foreground">
                    <p className="text-xs uppercase tracking-widest opacity-80">
                      Balance
                    </p>
                    <p className="font-display text-3xl font-bold">
                      {fmt(detailBalance)}
                    </p>
                    <div className="mt-2 flex gap-3 text-xs opacity-80">
                      <span>Total: {fmt(detailTotal)}</span>
                      <span>Paid: {fmt(detailPaid)}</span>
                    </div>
                  </div>
                );
              })()}

              {/* Items */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <h4 className="text-sm font-semibold">Items Ordered</h4>
                  {tabDetailOpen.items.some((i) => !i.issued) && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs gap-1"
                      onClick={handleIssueAll}
                    >
                      <PackageCheck className="h-3.5 w-3.5" /> Issue All
                    </Button>
                  )}
                </div>
                <div className="divide-y divide-border rounded-xl border border-border bg-card">
                  {tabDetailOpen.items.map((item, idx) => (
                    <div
                      key={item.itemId + idx}
                      className={`flex items-center gap-2 px-3 py-2 ${item.issued ? "opacity-60" : ""}`}
                    >
                      <span
                        className={`flex h-7 w-7 items-center justify-center rounded-md text-[10px] font-bold ${item.issued ? "bg-primary/10 text-primary" : "bg-warning/10 text-warning"}`}
                      >
                        {Number(item.qty)}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm">{item.name}</p>
                        <p className="text-[10px] text-muted-foreground">
                          by {item.serverName ?? "Staff"}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold">
                          {fmt(Number(item.total))}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {item.issued ? "✓ Issued" : "Pending"}
                        </p>
                      </div>
                      {!item.issued && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleIssueSingle(item.itemId);
                          }}
                          title="Issue this item"
                        >
                          <PackageCheck className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  ))}
                  {tabDetailOpen.items.length === 0 && (
                    <p className="px-3 py-2 text-sm text-muted-foreground">
                      No items
                    </p>
                  )}
                </div>
              </div>

              {/* Payments */}
              {tabDetailOpen.payments.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-1">Payments</h4>
                  <div className="divide-y divide-border rounded-xl border border-border bg-card">
                    {tabDetailOpen.payments.map((p) => (
                      <div
                        key={p.id}
                        className="flex items-center gap-2 px-3 py-2"
                      >
                        {p.method === "cash" ? (
                          <Banknote className="h-4 w-4 text-muted-foreground" />
                        ) : p.method === "mpesa" ? (
                          <Smartphone className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <CreditCard className="h-4 w-4 text-muted-foreground" />
                        )}
                        <p className="flex-1 text-sm capitalize">{p.method}</p>
                        <p className="text-sm font-semibold text-primary">
                          {fmt(Number(p.amount))}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="grid grid-cols-2 gap-2 pt-2">
                <Dialog
                  open={addItemDialogOpen}
                  onOpenChange={setAddItemDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="h-auto py-3 min-h-[48px]"
                    >
                      <Plus className="h-4 w-4" />
                      <span className="ml-1 text-sm">Add Item</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-h-[90vh] overflow-y-auto max-w-lg">
                    <DialogHeader>
                      <DialogTitle>
                        Add Items to {tabDetailOpen.customerName}'s Bill
                      </DialogTitle>
                    </DialogHeader>
                    <div className="relative mb-2">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        className="pl-9"
                        placeholder="Search menu..."
                        value={addItemSearch}
                        onChange={(e) => setAddItemSearch(e.target.value)}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2 max-h-52 overflow-y-auto pr-1">
                      {filteredAddItems.map((it) => (
                        <button
                          key={it.id}
                          onClick={() => handleAddToCart(it.id)}
                          className="rounded-lg border border-border bg-card p-2.5 text-left active:scale-[0.97] min-h-[64px]"
                        >
                          <p className="text-[9px] uppercase tracking-wider text-muted-foreground">
                            {it.sku}
                          </p>
                          <p className="line-clamp-2 text-xs font-medium leading-snug">
                            {it.name}
                          </p>
                          <div className="mt-1 flex items-center justify-between">
                            <span className="font-display text-xs font-semibold text-primary">
                              {fmt(it.sellingPrice)}
                            </span>
                            <span className="text-[10px] text-muted-foreground">
                              {it.stock} left
                            </span>
                          </div>
                        </button>
                      ))}
                      {filteredAddItems.length === 0 && (
                        <p className="col-span-2 py-4 text-center text-sm text-muted-foreground">
                          No items match "{addItemSearch}"
                        </p>
                      )}
                    </div>
                    {addItemCart.length > 0 && (
                      <div className="rounded-lg border border-border bg-card mt-2">
                        <div className="max-h-40 space-y-1 overflow-y-auto p-2">
                          {addItemCart.map((l) => (
                            <div
                              key={l.itemId}
                              className="flex items-center gap-1.5"
                            >
                              <p className="flex-1 truncate text-xs font-medium">
                                {l.name}
                              </p>
                              <button
                                onClick={() =>
                                  setAddItemCart((c) =>
                                    c
                                      .map((x) =>
                                        x.itemId === l.itemId
                                          ? {
                                              ...x,
                                              qty: Math.max(0, x.qty - 1),
                                            }
                                          : x,
                                      )
                                      .filter((x) => x.qty > 0),
                                  )
                                }
                                className="rounded bg-secondary p-1 min-h-[28px] min-w-[28px] flex items-center justify-center"
                              >
                                <Minus className="h-3 w-3" />
                              </button>
                              <span className="w-6 text-center text-xs font-bold tabular-nums">
                                {Number(l.qty)}
                              </span>
                              <button
                                onClick={() => handleAddToCart(l.itemId)}
                                className="rounded bg-secondary p-1 min-h-[28px] min-w-[28px] flex items-center justify-center"
                              >
                                <Plus className="h-3 w-3" />
                              </button>
                              <span className="w-14 text-right text-xs font-semibold tabular-nums">
                                {fmt(Number(l.qty) * Number(l.unitPrice))}
                              </span>
                              <button
                                onClick={() =>
                                  setAddItemCart((c) =>
                                    c.filter((x) => x.itemId !== l.itemId),
                                  )
                                }
                                className="rounded p-1 text-muted-foreground min-h-[28px] min-w-[28px] flex items-center justify-center"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className="border-t border-border p-2">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold">Total</span>
                            <span className="font-display text-sm font-bold">
                              {fmt(addItemCartTotal)}
                            </span>
                          </div>
                          <Button
                            className="w-full h-9 text-xs"
                            onClick={handleSubmitAddItems}
                          >
                            <ShoppingCart className="h-3.5 w-3.5" /> Add to Bill{" "}
                            {fmt(addItemCartTotal)}
                          </Button>
                        </div>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>

                <Button
                  variant="outline"
                  className="h-auto py-3 min-h-[48px]"
                  onClick={() => {
                    setPayTabSnapshot(tabDetailOpen);
                    setPayDialogOpen(true);
                  }}
                >
                  <Banknote className="h-4 w-4" />
                  <span className="ml-1 text-sm">Record Payment</span>
                </Button>

                {tabDetailOpen.items.some((i) => !i.issued) && (
                  <Button
                    variant="default"
                    className="h-auto py-3 min-h-[48px] col-span-2 bg-success hover:bg-success/90 text-success-foreground"
                    onClick={handleIssueAll}
                  >
                    <PackageCheck className="h-4 w-4" />
                    <span className="ml-1 text-sm">
                      Issue All Items & Register Sale
                    </span>
                  </Button>
                )}

                <Button
                  variant="outline"
                  className="h-auto py-3 min-h-[48px] col-span-2"
                  onClick={() => handlePrintInvoice(tabDetailOpen)}
                >
                  <FileText className="h-4 w-4" />
                  <span className="ml-1 text-sm">Print Invoice</span>
                </Button>

                {(() => {
                  const closeTotal = Number(
                    tabDetailOpen.items.reduce(
                      (a, i) => a + Number(i.total),
                      0,
                    ),
                  );
                  const closePaid = Number(
                    tabDetailOpen.payments.reduce(
                      (a, p) => a + Number(p.amount),
                      0,
                    ),
                  );
                  const closeBalance = closeTotal - closePaid;
                  const cannotClose =
                    tabDetailOpen.items.some((i) => !i.issued) ||
                    closeBalance > 0;
                  return (
                    <Button
                      variant={
                        tabDetailOpen.items.every((i) => i.issued) &&
                        closeBalance <= 0
                          ? "default"
                          : "secondary"
                      }
                      className="h-auto py-3 min-h-[48px] col-span-2"
                      disabled={cannotClose}
                      onClick={() => handleCloseTab(tabDetailOpen.id)}
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      <span className="ml-1 text-sm">
                        {closeBalance > 0
                          ? `Owes ${fmt(closeBalance)}`
                          : "Close Bill"}
                      </span>
                    </Button>
                  );
                })()}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog
        open={payDialogOpen}
        onOpenChange={(v) => {
          setPayDialogOpen(v);
          if (!v) setPayTabSnapshot(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Record Payment for{" "}
              {payTabSnapshot?.customerName || tabDetailOpen?.customerName}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Amount</Label>
              <Input
                type="number"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                placeholder="e.g. 500"
                autoFocus
              />
            </div>
            <div>
              <Label>Payment Method</Label>
              <div className="mt-1 grid grid-cols-3 gap-2">
                {paymentMethods.map((pm) => {
                  const Icon = pm.icon;
                  return (
                    <button
                      key={pm.value}
                      onClick={() => setPayMethod(pm.value)}
                      className={`flex flex-col items-center gap-1 rounded-lg border p-3 text-sm font-medium capitalize transition ${
                        payMethod === pm.value
                          ? "border-primary bg-accent text-accent-foreground"
                          : "border-border bg-card text-muted-foreground"
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      {pm.label}
                    </button>
                  );
                })}
              </div>
            </div>
            {(payTabSnapshot || tabDetailOpen) &&
              (() => {
                const pt = payTabSnapshot || tabDetailOpen;
                const payTotal = Number(
                  pt!.items.reduce((a, i) => a + Number(i.total), 0),
                );
                const payPaid = Number(
                  pt!.payments.reduce((a, p) => a + Number(p.amount), 0),
                );
                return (
                  <p className="text-center text-sm text-muted-foreground">
                    Balance: {fmt(payTotal - payPaid)}
                  </p>
                );
              })()}
          </div>
          <DialogFooter>
            <Button onClick={handlePay}>Record Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── CLOSED TABS (History) ───────────────────────────────────────── */}
      {closedTabs.length > 0 && (
        <section className="px-4 pt-8 pb-24">
          <div className="flex items-center gap-2 mb-3">
            <History className="h-4 w-4 text-muted-foreground" />
            <h2 className="font-display text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Closed Bills
            </h2>
          </div>
          <div className="space-y-2">
            {closedTabs.map((tab) => {
              const tabTotal = Number(
                tab.items.reduce((a, i) => a + Number(i.total), 0),
              );
              const tabPaid = Number(
                tab.payments.reduce((a, p) => a + Number(p.amount), 0),
              );
              const isOpen = closedTabOpen === tab.id;
              return (
                <div
                  key={tab.id}
                  className="rounded-xl border border-border bg-card overflow-hidden"
                >
                  <button
                    onClick={() => setClosedTabOpen(isOpen ? null : tab.id)}
                    className="w-full p-3 text-left active:scale-[0.98] min-h-[56px]"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">{tab.customerName}</p>
                      <div className="flex items-center gap-2">
                        <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">
                          {tab.items.length} items
                        </span>
                        {isOpen ? (
                          <ChevronUp className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                    <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
                      <span>
                        {new Date(Number(tab.createdAt)).toLocaleDateString(
                          "en-KE",
                          {
                            day: "numeric",
                            month: "short",
                          },
                        )}
                      </span>
                      <span>Paid: {fmt(tabPaid)}</span>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="border-t border-border px-3 py-3 space-y-3">
                      <div>
                        <h4 className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
                          Items Ordered
                        </h4>
                        <div className="divide-y divide-border rounded-lg border border-border">
                          {tab.items.map((item, idx) => (
                            <div
                              key={item.itemId + idx}
                              className="flex items-center gap-2 px-2 py-1.5"
                            >
                              <span className="flex h-6 w-6 items-center justify-center rounded bg-accent text-[10px] font-bold text-accent-foreground">
                                {Number(item.qty)}
                              </span>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs">{item.name}</p>
                                <p className="text-[9px] text-muted-foreground">
                                  by {item.serverName ?? "Staff"}
                                </p>
                              </div>
                              <p className="text-xs font-semibold">
                                {fmt(Number(item.total))}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {tab.payments.length > 0 && (
                        <div>
                          <h4 className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
                            Payments
                          </h4>
                          <div className="divide-y divide-border rounded-lg border border-border">
                            {tab.payments.map((p) => (
                              <div
                                key={p.id}
                                className="flex items-center gap-2 px-2 py-1.5"
                              >
                                {p.method === "cash" ? (
                                  <Banknote className="h-3.5 w-3.5 text-muted-foreground" />
                                ) : p.method === "mpesa" ? (
                                  <Smartphone className="h-3.5 w-3.5 text-muted-foreground" />
                                ) : (
                                  <CreditCard className="h-3.5 w-3.5 text-muted-foreground" />
                                )}
                                <p className="flex-1 text-xs capitalize">
                                  {p.method}
                                </p>
                                <p className="text-xs font-semibold text-primary">
                                  {fmt(Number(p.amount))}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-between pt-1">
                        <div className="text-xs text-muted-foreground">
                          <span>Total: {fmt(tabTotal)}</span>
                          <span className="ml-2">Paid: {fmt(tabPaid)}</span>
                          {tabPaid >= tabTotal && (
                            <span className="ml-2 text-primary font-semibold">
                              ✓ Settled
                            </span>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs gap-1"
                          onClick={() => handlePrintClosedReceipt(tab)}
                        >
                          <Printer className="h-3.5 w-3.5" /> Receipt
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
